import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Study } from '@models/study.model';
import { StudiesFilter } from '@models/studiesFilter.model';
import { LoggingService } from '@services/logging.service';
import { StudyDatabase } from '@models/studyDatabase.model';
import { Router } from '@angular/router';
import { GraphService } from '@services/graph.service';
import { SearchResultsFilter } from '@models/searchResultsFilter.model';
import { saveAs } from 'file-saver-es';
import { KeywordsExtracted } from '@models/keywordsExtracted.model';
import { KeywordsSelected } from '@models/keywordsSelected.model';
import { FunctionKeyword } from '@models/functionKeyword.model';
import { FunctionCategory } from '@models/functionCategory.model';
import { StudySearchStrategy } from '@models/searchStrategy.model';
import { BusinessDescriptionReview } from '@models/businessDescriptionReview.model';
import { QuerySearchIndex } from '@models/querySearchIndex.model';
import { SearchResultsKeyword } from '@models/searchResultsKeyword.model';
import { GetLeverageStudy } from '@models/getLeverageStudy.model';
import { Ratio } from '../models/ratio.model';
import { UserNotificationService } from '@services/user-notification.service';
import { StudyViews } from '../models/studyViews.model';
import { UserStudy } from '../models/userStudy.model';
import { Observable, firstValueFrom } from 'rxjs';
import { Lookup } from '../models/lookup.model';
import _ from 'lodash-es';
import { SteppedSearchMatrix } from '../models/steppedSearchMatrix.model';
import { StudySearchFilter } from '@models/studySearchFilter.model';
import { SearchStudy } from '../models/search-study.model';
import { SearchStrategyUpdateResponse } from '../mystudies/analysis/search-strategy/models/search-strategy-update-response.model';
import { SearchStrategyUpdate } from '../mystudies/analysis/search-strategy/models/search-strategy-update.model';
import { SearchStrategyCreate } from '../mystudies/analysis/search-strategy/models/search-strategy-create.model';
import { StudySearchStrategyProcess } from '../mystudies/_study_constants';
import { ContemporaryReportType } from '../mystudies/study-reports/study-reports-modal/contemporary-report-type.enum';

@Injectable()
export class StudyService {
  baseUrl: string;
  isSearchStudy: boolean;
  constructor(
    private http: HttpClient,
    private router: Router,
    private graphService: GraphService,
    private logger: LoggingService,
    private userNotificationService: UserNotificationService,
  ) {}

  public async getMyStudies(filter: StudiesFilter): Promise<SearchStudy[]> {
    this.logger.info(this.constructor.name, 'GetMyStudies', 'Start');
    let studies: SearchStudy[] = [];
    filter.isSearchStudy = this.isSearchStudy;
    studies = <SearchStudy[]>await firstValueFrom(this.http.post<SearchStudy[]>('/api/study/filter', filter)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetMyStudies', e);
      if (e.status === 401) {
        this.router.navigate(['nostudyaccess']);
      }
    });

    if (!studies) {
      studies = [];
    }

    return studies;
  }

  public async getMyStudiesCount(filter: StudiesFilter): Promise<number> {
    this.logger.info(this.constructor.name, 'GetMyStudiesCount', 'Start');
    filter.isSearchStudy = this.isSearchStudy;
    const count = <number>await firstValueFrom(this.http.post('/api/study/filtercount', filter)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetMyStudies', e);
      if (e.status === 401) {
        this.router.navigate(['nostudyaccess']);
      }
    });

    return count;
  }

  public GetStudyById$(studyId: number): Observable<Study> {
    this.logger.info(this.constructor.name, 'GetStudyById', `Start; studyId=[${studyId}]`);
    return this.http.get<Study>(`/api/study/${studyId}`) as Observable<Study>;
  }

  public async GetStudyById(studyId: number): Promise<Study> {
    this.logger.info(this.constructor.name, 'GetStudyById', `Start; studyId=[${studyId}]`);
    const study: Study = <Study>await firstValueFrom(this.GetStudyById$(studyId)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetStudyById', e);
      if (e.status === 401) {
        this.router.navigate(['nostudyaccess']);
      }
    });
    return study;
  }

  public async GetStudyDatabaseOptionsByDate(asOfDate: string, studyid: number, onlyLatest: boolean): Promise<StudyDatabase[]> {
    this.logger.info(this.constructor.name, 'GetStudyDatabaseOptionsByDate', 'Start');
    const studyDatabases: StudyDatabase[] = <StudyDatabase[]>await firstValueFrom(
      this.http.get<StudyDatabase[]>(`/api/study/StudyDatabaseByDate/${asOfDate}/${studyid}/${onlyLatest}`),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetStudyDatabaseOptionsByDate', e);
      if (e.status === 401) {
        this.router.navigate(['nostudyaccess']);
      }
    });
    return studyDatabases;
  }

  public async SetStudyLock(isLocked: boolean, studyId: number): Promise<boolean> {
    let flag = false;

    await firstValueFrom(this.http.patch('/api/study/SetStudyLock', { studyId: studyId, isLocked: isLocked })).then(() => {
      flag = true;
    });

    return flag;
  }

  public async Save(study: Study): Promise<Study> {
    this.logger.info(this.constructor.name, 'Save', 'Start', study);

    // need GraphUser data attributes for potentially new users added as Study Members
    // Bug 265890: PROD environment: TPBM.User rows added via a Study do not have property values set correctly
    for (let i = 0; i < study.userStudies.length; i++) {
      const rowData = study.userStudies[i];
      //BUG 935214 Prod- INC5424165 - If any study preparer left the organization, then other contributors are unable to add the members in the study.
      //Fix: skip fetching the GraphUser data for the non exixting users.
      const specificUser = await this.graphService.getSpecificUser(study.userStudies[i].user.emailAddress).catch((e) => {
        return;
      });
      rowData['GraphUser'] = specificUser;
    }

    let savedStudy = null;
    if (study.studyId) {
      // Update
      savedStudy = <Study>await firstValueFrom(this.http.put<Study>('/api/study', study)).catch((e) => {
        this.logger.httpError(this.constructor.name, 'Save(put/update)', e);
        if (e.status === 401) {
          this.router.navigate(['nostudyaccess']);
        }
      });
    } else {
      // Add
      savedStudy = <Study>await firstValueFrom(this.http.post<Study>('/api/study', study)).catch((e) => {
        this.logger.httpError(this.constructor.name, 'Save(post/add)', e);
        if (e.status === 401) {
          this.router.navigate(['nostudyaccess']);
        }
      });
    }
    return savedStudy;
  }

  public async executeStudySearchStrategyUpdated(searchStrategy: StudySearchStrategy) {
    await firstValueFrom(this.http.post<StudySearchStrategy>(`/api/study/ExecuteSearchStrategiesUpdated`, searchStrategy)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'ExecuteStudySearchStrategyUpdated', e);
      if (e.status === 401) {
        this.router.navigate(['nostudyaccess']);
      }
    });
  }

  public async SearchResultsUpdated(filter: SearchResultsFilter): Promise<BusinessDescriptionReview[]> {
    this.logger.info(this.constructor.name, 'SearchResultsUpdated', 'Start');
    let searchResults: BusinessDescriptionReview[] = [];
    searchResults = <BusinessDescriptionReview[]>await firstValueFrom(
      this.http.post<BusinessDescriptionReview[]>('/api/study/searchresultsupdated', filter),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'SearchResultsUpdated', e);
      if (e.status === 401) {
        this.router.navigate(['nostudyaccess']);
      }
    });

    if (!searchResults) {
      searchResults = [];
    } else {
      // this.logger.info(this.constructor.name, 'SearchResultsUpdated', 'Results', searchResults);
    }

    return searchResults;
  }

  // to fix issue of Converting circular structure to JSON
  JSONStringify(data: any) {
    let returnData = data;

    try {
      let cache = [];
      returnData = JSON.stringify(data, function (key, value) {
        if (typeof value === 'object' && value !== null) {
          if (cache.indexOf(value) !== -1) {
            // Circular reference found, discard key
            return;
          }
          // Store value in our collection
          cache.push(value);
        }
        return value;
      });
      cache = null; // Enable garbage collection
    } catch (ex) {
      this.logger.error(this.constructor.name, 'JSONStringify', ex);
    }

    return returnData;
  }

  async ExportToExcel(searchStrategy: StudySearchStrategy): Promise<boolean> {
    this.logger.info(this.constructor.name, 'ExportToExcel', 'Start');

    const result = await firstValueFrom(
      this.http.post('/api/study/export/', searchStrategy, {
        responseType: 'blob',
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        observe: 'response',
      }),
    )
      .then((response) => {
        let filename = 'Temp_';
        const contentDispositionHeader = response.headers.get('Content-Disposition');

        if (contentDispositionHeader) {
          filename = contentDispositionHeader.split(';')[1].trim().split('=')[1];
        }

        const blob = new Blob([response.body], { type: response.body.type.toString() });
        saveAs(blob, filename);

        return true;
      })
      .catch((e) => {
        this.logger.httpError(this.constructor.name, 'ExportToExcel', e);

        if (e.status === 404) {
          return null;
        }

        return false;
      });

    return result;
  }

  async GetAllActiveFunctionKeywords(): Promise<FunctionCategory[]> {
    this.logger.info(this.constructor.name, 'GetAllActiveFunctionKeywords', 'Start');
    let categories: FunctionCategory[];
    try {
      categories = <FunctionCategory[]>await firstValueFrom(this.http.get<FunctionCategory[]>('/api/master/function/category')).catch((e) => {
        this.logger.httpError(this.constructor.name, 'GetAllActiveFunctionKeywords_FunctionCategory', e);
      });

      const keywords = <FunctionKeyword[]>await firstValueFrom(this.http.get<FunctionKeyword[]>('/api/master/function/keyword')).catch((e) => {
        this.logger.httpError(this.constructor.name, 'GetAllActiveFunctionKeywords_FunctionKeyword', e);
      });

      this.logger.info(this.constructor.name, 'GetAllActiveFunctionKeywords', `forEach: ${categories.length} items`);
      categories.forEach((category) => (category.functionKeywords = keywords.filter((k) => k.functionCategoryId === category.functionCategoryId)));
      return categories;
    } finally {
      this.logger.info(this.constructor.name, 'GetAllActiveFunctionKeywords', `End: ${categories.length} items`);
    }
  }

  async SaveSelectedKeywords(selectedKeywords: KeywordsSelected[]): Promise<void> {
    this.logger.info(this.constructor.name, 'SaveSelectedKeywords', 'Start');

    await firstValueFrom(this.http.post('/api/study/StudySearchStrategy/SaveBatchKeywordsSelected', selectedKeywords)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'SaveSelectedKeywords', e);
    });
  }

  async AddCustomKeyword(extractedKeyword: KeywordsExtracted): Promise<KeywordsExtracted> {
    this.logger.info(this.constructor.name, 'AddCustomKeyword', 'Start');

    const result = <KeywordsExtracted>await firstValueFrom(
      this.http.post<KeywordsExtracted>('/api/study/StudySearchStrategy/AddCustomKeyword', extractedKeyword),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'AddCustomKeyword', e);
    });

    return result;
  }

  public addToSteppedSearch(steppedSearchMatrix: SteppedSearchMatrix[]): Observable<void> {
    return this.http.post('/api/study/AddToSteppedSearch', steppedSearchMatrix) as Observable<any>;
  }

  public getSteppedSearchMatrix(searchStrategyId: number, studySearchFilters: StudySearchFilter[]): Observable<SteppedSearchMatrix[]> {
    const payload = {
      searchStrategyId: searchStrategyId,
      studySearchFilters: studySearchFilters,
    };
    return this.http.post<SteppedSearchMatrix[]>('/api/study/GetSteppedSearchMatrix', payload) as Observable<SteppedSearchMatrix[]>;
  }

  public deleteFromSteppedSearch(steppedSearchMatrixIds: number[]): Observable<void> {
    return this.http.post('/api/study/DeleteFromSteppedSearch', steppedSearchMatrixIds) as Observable<any>;
  }

  async deleteExtractedKeywords(searchStrategyId: number): Promise<void> {
    this.logger.info(this.constructor.name, 'SaveSelectedKeywords', 'Start');

    await firstValueFrom(this.http.delete(`/api/study/StudySearchStrategy/${searchStrategyId}/DeleteExtractedKeywords`)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'SaveSelectedKeywords', e);
    });
  }

  public deleteExtractedKeywords$(searchStrategyId: number): Observable<void> {
    return this.http.delete<void>(`/api/study/StudySearchStrategy/${searchStrategyId}/DeleteExtractedKeywords`);
  }

  public async UpdateTestedParty(study: Study): Promise<Study> {
    this.logger.info(this.constructor.name, 'UpdateTestedParty', 'Start', study);

    let savedStudy = null;

    if (study.studyId) {
      // Update
      savedStudy = <Study>await firstValueFrom(this.http.post<Study>('/api/study/UpdateTestedParty', study)).catch((e) => {
        this.logger.httpError(this.constructor.name, 'UpdateTestedParty', e);
        if (e.status === 401) {
          this.router.navigate(['nostudyaccess']);
        }
      });
    }

    return savedStudy;
  }

  public async UpdateUserStudy(userStudies: UserStudy[]): Promise<Study> {
    this.logger.info(this.constructor.name, 'UpdateUserStudy', 'Start', userStudies);

    let savedStudy = null;

    // need GraphUser data attributes for potentially new users added as Study Members
    // Bug 265890: PROD environment: TPBM.User rows added via a Study do not have property values set correctly
    for (let i = 0; i < userStudies.length; i++) {
      const rowData = userStudies[i];
      //BUG 935214 Prod- INC5424165 - If any study preparer left the organization, then other contributors are unable to add the members in the study.
      //Fix: only get GraphUser data for the newly added user.
      if (!userStudies[i].userStudyId) {
        const specificUser = await this.graphService.getSpecificUser(userStudies[i].user.emailAddress);
        rowData['GraphUser'] = specificUser;
      }
    }
    // Update
    savedStudy = <Study>await firstValueFrom(this.http.post<Study>('/api/study/UpdateUserStudy', userStudies)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'UpdateUserStudy', e);
      if (e.status === 401) {
        this.router.navigate(['nostudyaccess']);
      }
    });

    return savedStudy;
  }

  public updateSearchStrategy$(searchStrategyId: number, data: SearchStrategyUpdate): Observable<SearchStrategyUpdateResponse> {
    return this.http.post<SearchStrategyUpdateResponse>(`/api/study/UpdateSearchStrategy/${searchStrategyId}`, data);
  }

  public async validateSearchStrategyForMultiuser(searchStrategyId: number, data: SearchStrategyUpdate): Promise<any> {
    this.logger.info(this.constructor.name, 'validateSearchStrategyForMultiuser', 'Start');
    const result = <any>await firstValueFrom(this.http.post<any>(`/api/study/ValidateSearchStrategyForMultiuser/${searchStrategyId}`, data)).catch(
      (e) => {
        this.logger.httpError(this.constructor.name, 'validateSearchStrategyForMultiuser', e);
      },
    );
    return result;
  }

  public addSearchStrategy$(studyId: number, data: SearchStrategyCreate): Observable<StudySearchStrategy> {
    return this.http.post<StudySearchStrategy>(`/api/study/AddSearchStrategy/${studyId}`, data);
  }

  public async addUpdateSearchStrategy(study: Study): Promise<Study> {
    this.logger.info(this.constructor.name, 'AddUpdateSearchStrategy', 'Start', study);

    let savedStudy = null;

    if (study.studyId) {
      // Update
      savedStudy = <Study>await firstValueFrom(this.http.post<Study>('/api/study/AddUpdateSearchStrategy', study)).catch((e) => {
        this.logger.httpError(this.constructor.name, 'AddUpdateSearchStrategy', e);
        if (e.status === 401) {
          this.router.navigate(['nostudyaccess']);
        }
      });
    }
    return savedStudy;
  }

  public async getKeywordExtractionStatus(searchStrategyId: number): Promise<StudySearchStrategyProcess> {
    let keywordSearchStatus: StudySearchStrategyProcess = StudySearchStrategyProcess.NotStarted;
    if (searchStrategyId) {
      keywordSearchStatus = <StudySearchStrategyProcess>await firstValueFrom(
        this.http.get<StudySearchStrategyProcess>(`/api/Study/StudySearchStrategy/${searchStrategyId}/KeywordExtractionStatus`),
      ).catch((e) => {
        this.logger.httpError(this.constructor.name, 'GetKeywordExtractionStatus', e);
      });
    }
    return keywordSearchStatus;
  }

  public async GetContentIndexStatus(searchStrategyId: number): Promise<StudySearchStrategyProcess> {
    this.logger.info(this.constructor.name, 'GetContentIndexStatus', 'Start');

    let contentIndexStatus: StudySearchStrategyProcess = StudySearchStrategyProcess.NotStarted;

    try {
      if (searchStrategyId) {
        contentIndexStatus = <StudySearchStrategyProcess>await firstValueFrom(
          this.http.get<StudySearchStrategyProcess>(`/api/Study/StudySearchStrategy/${searchStrategyId}/ContentIndexStatus`),
        ).catch((e) => {
          this.logger.httpError(this.constructor.name, 'GetKeywordExtractionStatus', e);
        });
      }
      return contentIndexStatus;
    } finally {
      this.logger.info(this.constructor.name, 'GetKeywordExtractionStatus', 'End');
    }
  }

  public async GetBusinessDescriptionReview(
    searchStrategyId: number,
    pageSize: number,
    pageIndex: number,
    comparableStatusId,
  ): Promise<BusinessDescriptionReview[]> {
    this.logger.info(this.constructor.name, 'GetBusinessDescriptionReview', 'Start');

    const request = {
      studySearchStrategyId: searchStrategyId,
      pageSize: pageSize,
      pageIndex: pageIndex,
      comparableStatusId: comparableStatusId,
    };

    const bdReview = <BusinessDescriptionReview[]>await firstValueFrom(
      this.http.post<BusinessDescriptionReview[]>('/api/Study/GetBusinessDescriptionReview', request),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetBusinessDescriptionReview', e);
    });

    return bdReview;
  }

  public async QuerySearchIndex(querySearchIndex: QuerySearchIndex): Promise<number> {
    this.logger.info(this.constructor.name, 'QuerySearchIndex', 'Start');

    const count = <number>await firstValueFrom(this.http.post<number>('/api/Study/QuerySearchIndex', querySearchIndex)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'QuerySearchIndex', e);
    });

    return count;
  }

  public async GetBusinessDataCountBySearchStrategyId(studySearchStrategyId: number): Promise<number> {
    this.logger.info(this.constructor.name, 'GetBusinessDataCountBySearchStrategyId', 'Start');
    const count = <number>await firstValueFrom(
      this.http.get<number>(`/api/study/GetBusinessDataCountBySearchStrategyId/${studySearchStrategyId}`),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetBusinessDataCountBySearchStrategyId', e);
    });
    return count;
  }

  public async UpdateSearchResultsKeyword(searchResultsKeyword: SearchResultsKeyword): Promise<SearchResultsKeyword> {
    this.logger.info(this.constructor.name, 'UpdateSearchResultsKeyword', 'Start');

    const result = <SearchResultsKeyword>await firstValueFrom(
      this.http.put<SearchResultsKeyword>('/api/Study/UpdateSearchResultsKeyword', searchResultsKeyword),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'UpdateSearchResultsKeyword', e);
    });

    return result;
  }

  public async GetStudyForLeverage(studyId: number): Promise<GetLeverageStudy> {
    this.logger.info(this.constructor.name, 'GetStudyForLeverage', 'Start');

    const result = <GetLeverageStudy>await firstValueFrom(this.http.get<GetLeverageStudy>(`/api/Study/GetStudyForLeverage/${studyId}`)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetStudyForLeverage', e);
    });

    return result;
  }
  public GetStudyForLeverage$(studyId: number): Observable<GetLeverageStudy> {
    this.logger.info(this.constructor.name, 'GetStudyForLeverage', 'Start');

    return this.http.get<GetLeverageStudy>(`/api/Study/GetStudyForLeverage/${studyId}`) as Observable<GetLeverageStudy>;
  }

  public async processLeveragedStudy(sourceStudyId: number, destinationStudyId: number, copyReclass: boolean): Promise<Study> {
    this.logger.info(this.constructor.name, 'ProcessLeveragedStudy', 'Start');

    const request = {
      sourceStudyId,
      destinationStudyId,
      copyReclass,
    };

    const result = <Study>await firstValueFrom(this.http.post<Study>('/api/Study/ProcessLeveragedStudy', request)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'ProcessLeveragedStudy', e);
    });

    return result;
  }

  public async GetBDMatrixBySearchStrategyId(searchStrategyId: number): Promise<number> {
    this.logger.info(this.constructor.name, 'GetBDMatrixBySearchStrategyId', 'Start');

    const result = <number>await firstValueFrom(this.http.get<number>(`/api/BDMatrix/GetBDMatrixCountBySearchStrategyId/${searchStrategyId}`)).catch(
      (e) => {
        this.logger.httpError(this.constructor.name, 'GetBDMatrixBySearchStrategyId', e);
      },
    );
    return result;
  }

  public async getMissingFinancialYearsForSelectedFeed(studyId: number, databases: any): Promise<any[]> {
    this.logger.info(this.constructor.name, 'getMissingFinancialYearsForSelectedFeed', 'Start');
    const result = <any[]>await firstValueFrom(
      this.http.post<any[]>(`/api/study/GetMissingFinancialYearsForSelectedFeed`, { databases: databases, StudyId: studyId }),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'getMissingFinancialYearsForSelectedFeed', e);
    });
    return result;
  }

  public async GetSearchResultsKeywordBySearchStrategyId(searchStrategyId: number): Promise<number> {
    this.logger.info(this.constructor.name, 'GetSearchResultsKeywordBySearchStrategyId', 'Start');

    const result = <number>await firstValueFrom(
      this.http.get<number>(`/api/Study/GetSearchResultsKeywordBySearchStrategyId/${searchStrategyId}`),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetSearchResultsKeywordBySearchStrategyId', e);
    });

    return result;
  }

  public async ReportSearchStrategy(searchStrategy: StudySearchStrategy, isFinal: boolean, columnSelections: any): Promise<boolean> {
    this.logger.info(this.constructor.name, 'ReportSearchStrategy', 'Start');

    if (searchStrategy.studyId === undefined) {
      // caller should be handling this now, but I'll go ahead and leave this in here...
      this.userNotificationService.addUpdateNotification(
        'ReportSearchStrategy',
        `Search Strategy ${isFinal ? 'Final' : 'Preliminary'} Report: Search strategy does not exist.`,
        true,
      );
      return true;
    }

    const notificationBody = `Study ${searchStrategy.studyId} Search Strategy ${isFinal ? 'Final' : 'Preliminary'} Report`;
    const reportParametersSearchStrategyDTO = {
      isFinal: isFinal,
      columnSelectionsDTO: columnSelections,
      studySearchStrategyId: searchStrategy.studySearchStrategyId,
      studyId: searchStrategy.studyId,
    };

    await firstValueFrom(
      this.http.post('/api/study/ReportSearchStrategy/', reportParametersSearchStrategyDTO, {
        responseType: 'blob',
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        observe: 'response',
      }),
    )
      .then()
      .catch((e) => {
        if (e.status === 404) {
          this.userNotificationService.addNotification(`${notificationBody}: No data found.`, false);
        } else {
          this.logger.httpError(this.constructor.name, 'ReportSearchStrategy', e);
          this.userNotificationService.addUpdateNotification('ReportSearchStrategy', `${notificationBody}: ${e.message}`, true);
        }
      });

    return true;
  }

  public async GetFinancialRatioByStudyId(studyId: number): Promise<Ratio[]> {
    this.logger.info(this.constructor.name, 'GetFinancialRatioByStudyId', 'Start');

    const result = <Ratio[]>await firstValueFrom(this.http.get<Ratio[]>(`/api/Study/GetFinancialRatioByStudyId/${studyId}`)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetFinancialRatioByStudyId', e);
    });

    return result;
  }

  public async ReportKeywordScreening(searchStrategy: StudySearchStrategy, isFinal: boolean, columnSelections: any): Promise<boolean> {
    this.logger.info(this.constructor.name, 'ReportKeywordScreening', 'Start');

    if (searchStrategy.studyId === undefined) {
      // caller should be handling this now, but I'll go ahead and leave this in here...
      this.userNotificationService.addUpdateNotification(
        'ReportKeywordScreening',
        `Keyword Screening ${isFinal ? 'Final' : 'Preliminary'} Report: Keyword Screening does not exist.`,
        true,
      );
      return true;
    }

    const notificationBody = `Study ${searchStrategy.studyId} Keyword Screening ${isFinal ? 'Final' : 'Preliminary'} Report`;

    const reportParametersSearchStrategyDTO = {
      isFinal: isFinal,
      columnSelectionsDTO: columnSelections,
      studySearchStrategyId: searchStrategy.studySearchStrategyId,
      studyId: searchStrategy.studyId,
    };

    await firstValueFrom(
      this.http.post('/api/study/ReportKeywordScreening/', reportParametersSearchStrategyDTO, {
        responseType: 'blob',
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        observe: 'response',
      }),
    )
      .then()
      .catch((e) => {
        if (e.status === 404) {
          this.userNotificationService.addNotification(`${notificationBody}: No data found.`, false);
        } else {
          this.logger.httpError(this.constructor.name, 'ReportKeywordScreening', e);
          this.userNotificationService.addUpdateNotification('ReportKeywordScreening', `${notificationBody}: ${e.message}`, true);
        }
      });

    return true;
  }

  public async ReportAcceptedCompanies(searchStrategy: StudySearchStrategy, isFinal: boolean, columnSelections: any): Promise<boolean> {
    this.logger.info(this.constructor.name, 'ReportAcceptedCompanies', 'Start');

    if (searchStrategy.studyId === undefined) {
      // caller should be handling this now, but I'll go ahead and leave this in here...
      this.userNotificationService.addUpdateNotification(
        'ReportAcceptedCompanies',
        `Accepted Companies ${isFinal ? 'Final' : 'Preliminary'} Report: Accepted Companies does not exist.`,
        true,
      );
      return true;
    }

    const notificationBody = `Study ${searchStrategy.studyId} Accepted Companies ${isFinal ? 'Final' : 'Preliminary'} Report`;

    const reportParametersSearchStrategyDTO = {
      isFinal: isFinal,
      columnSelectionsDTO: columnSelections,
      studySearchStrategyId: searchStrategy.studySearchStrategyId,
      studyId: searchStrategy.studyId,
    };

    await firstValueFrom(
      this.http.post('/api/study/ReportAcceptedCompanies/', reportParametersSearchStrategyDTO, {
        responseType: 'blob',
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        observe: 'response',
      }),
    )
      .then()
      .catch((e) => {
        if (e.status === 404) {
          this.userNotificationService.addNotification(`${notificationBody}: No data found.`, false);
        } else {
          this.logger.httpError(this.constructor.name, 'ReportAcceptedCompanies', e);
          this.userNotificationService.addUpdateNotification('ReportAcceptedCompanies', `${notificationBody}: ${e.message}`, true);
        }
      });

    return true;
  }

  public async ReportIRMatrix(searchStrategy: StudySearchStrategy, isFinal: boolean, columnSelections: any): Promise<boolean> {
    this.logger.info(this.constructor.name, 'ReportIRMatrix', 'Start');

    if (searchStrategy.studyId === undefined) {
      // caller should be handling this now, but I'll go ahead and leave this in here...
      this.userNotificationService.addUpdateNotification(
        'ReportIRMatrix',
        `Internet Review Matrix ${isFinal ? 'Final' : 'Preliminary'} Report: Internet Review does not exist.`,
        true,
      );
      return true;
    }

    const notificationBody = `Study ${searchStrategy.studyId} Internet Review Matrix ${isFinal ? 'Final' : 'Preliminary'} Report`;

    const reportParametersSearchStrategyDTO = {
      isFinal: isFinal,
      columnSelectionsDTO: columnSelections,
      studySearchStrategyId: searchStrategy.studySearchStrategyId,
      studyId: searchStrategy.studyId,
    };

    await firstValueFrom(
      this.http.post('/api/study/ReportIRMatrix/', reportParametersSearchStrategyDTO, {
        responseType: 'blob',
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        observe: 'response',
      }),
    )
      .then()
      .catch((e) => {
        if (e.status === 404) {
          this.userNotificationService.addNotification(`${notificationBody}: No data found.`, false);
        } else {
          this.logger.httpError(this.constructor.name, 'ReportIRMatrix', e);
          this.userNotificationService.addUpdateNotification('ReportIRMatrix', `${notificationBody}: ${e.message}`, true);
        }
      });

    return true;
  }

  public async ReportAcceptedRejectedCompanies(searchStrategy: StudySearchStrategy, isFinal: boolean, columnSelections: any): Promise<boolean> {
    this.logger.info(this.constructor.name, 'ReportAcceptedCompanies', 'Start');

    if (searchStrategy.studyId === undefined) {
      // caller should be handling this now, but I'll go ahead and leave this in here...
      this.userNotificationService.addUpdateNotification(
        'ReportAcceptedRejectedCompanies',
        `Accepted & Rejected Companies ${isFinal ? 'Final' : 'Preliminary'} Report: Accepted & Rejected Companies does not exist.`,
        true,
      );
      return true;
    }

    const notificationBody = `Study ${searchStrategy.studyId} Accepted & Rejected Companies ${isFinal ? 'Final' : 'Preliminary'} Report`;

    const reportParametersSearchStrategyDTO = {
      isFinal: isFinal,
      columnSelectionsDTO: columnSelections,
      studySearchStrategyId: searchStrategy.studySearchStrategyId,
      studyId: searchStrategy.studyId,
    };

    await firstValueFrom(
      this.http.post('/api/study/ReportAcceptedRejectedCompanies/', reportParametersSearchStrategyDTO, {
        responseType: 'blob',
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        observe: 'response',
      }),
    )
      .then()
      .catch((e) => {
        if (e.status === 404) {
          this.userNotificationService.addNotification(`${notificationBody}: No data found.`, false);
        } else {
          this.logger.httpError(this.constructor.name, 'ReportAcceptedRejectedCompanies', e);
          this.userNotificationService.addUpdateNotification('ReportAcceptedRejectedCompanies', `${notificationBody}: ${e.message}`, true);
        }
      });

    return true;
  }

  public async ContemporaryFinancialSummaryReport(
    searchStrategy: StudySearchStrategy,
    testedPartyId: number,
    contemporaryReportType: ContemporaryReportType,
  ): Promise<boolean> {
    if (searchStrategy.studyId === undefined) {
      // caller should be handling this now, but I'll go ahead and leave this in here...
      this.userNotificationService.addUpdateNotification(
        'ContemporaryFinancialSummaryReport',
        `Contemporary Financial Summary Report: does not exist.`,
        true,
      );
      return true;
    }

    const notificationBody = `Study ${searchStrategy.studyId} Contemporary Financial Summary Report`;

    const reportParametersContemporaryFinancialSummaryDTO = {
      studySearchStrategyId: searchStrategy.studySearchStrategyId,
      studyId: searchStrategy.studyId,
      testedPartyId: testedPartyId,
      contemporaryReportType: contemporaryReportType,
    };

    await firstValueFrom(
      this.http.post('/api/MasterMatrix/ContemporaryFinancialSummaryReport/', reportParametersContemporaryFinancialSummaryDTO, {
        responseType: 'blob',
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        observe: 'response',
      }),
    )
      .then()
      .catch((e) => {
        if (e.status === 404) {
          this.userNotificationService.addNotification(`${notificationBody}: No data found.`, false);
        } else {
          this.logger.httpError(this.constructor.name, 'ContemporaryFinancialSummaryReport', e);
          this.userNotificationService.addUpdateNotification('ContemporaryFinancialSummaryReport', `${notificationBody}: ${e.message}`, true);
        }
      });

    return true;
  }

  public async StudyByDateRangeReport(fromDate: string, toDate: string, optionalColumnsString: string): Promise<boolean> {
    const notificationBody = `Study by date range Report`;

    const StudyDeailsByDateRangeReportDto = {
      fromDate: fromDate,
      toDate: toDate,
      optionalColumnsString: optionalColumnsString,
    };

    await firstValueFrom(
      this.http.post('/api/study/GetStudyDetailsByDateRange/', StudyDeailsByDateRangeReportDto, {
        responseType: 'blob',
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        observe: 'response',
      }),
    )
      .then()
      .catch((e) => {
        if (e.status === 404) {
          this.userNotificationService.addNotification(`${notificationBody}: No data found.`, false);
        } else {
          this.logger.httpError(this.constructor.name, 'StudyByDateRangeReport', e);
          this.userNotificationService.addUpdateNotification('StudyByDateRangeReport', `${notificationBody}: ${e.message}`, true);
        }
      });

    return true;
  }

  public async ReportSearchWriteUp(searchStrategy: StudySearchStrategy): Promise<boolean> {
    if (searchStrategy.studyId === undefined) {
      // caller should be handling this now, but I'll go ahead and leave this in here...
      this.userNotificationService.addUpdateNotification('ReportSearchWriteUp', `Search Write-up Report: does not exist.`, true);
      return true;
    }

    const notificationBody = `Study ${searchStrategy.studyId} Search Write-up Report`;

    const reportParametersSearchWriteUpDTO = {
      studySearchStrategyId: searchStrategy.studySearchStrategyId,
      studyId: searchStrategy.studyId,
    };

    await firstValueFrom(
      this.http.post('/api/study/ReportSearchWriteUp/', reportParametersSearchWriteUpDTO, {
        responseType: 'blob',
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        observe: 'response',
      }),
    )
      .then()
      .catch((e) => {
        if (e.status === 404) {
          this.userNotificationService.addNotification(`${notificationBody}: No data found.`, false);
        } else {
          this.logger.httpError(this.constructor.name, 'ReportSearchWriteUp', e);
          this.userNotificationService.addUpdateNotification('ReportSearchWriteUp', `${notificationBody}: ${e.message}`, true);
        }
      });

    return true;
  }

  public async ReportEliminationChart(searchStrategy: StudySearchStrategy, isFinal: boolean, columnSelections: any): Promise<boolean> {
    this.logger.info(this.constructor.name, 'ReportEliminationChart', 'Start');

    if (searchStrategy.studyId === undefined) {
      // caller should be handling this now, but I'll go ahead and leave this in here...
      this.userNotificationService.addUpdateNotification(
        'ReportEliminationChart',
        `Elimination Chart ${isFinal ? 'Final' : 'Preliminary'} Report: Elimination Chart does not exist.`,
        true,
      );
      return true;
    }

    const notificationBody = `Study ${searchStrategy.studyId} Elimination Chart ${isFinal ? 'Final' : 'Preliminary'} Report`;

    const reportParametersSearchStrategyDTO = {
      isFinal: isFinal,
      columnSelectionsDTO: columnSelections,
      studySearchStrategyId: searchStrategy.studySearchStrategyId,
      studyId: searchStrategy.studyId,
    };

    await firstValueFrom(
      this.http.post('/api/study/ReportEliminationChart/', reportParametersSearchStrategyDTO, {
        responseType: 'blob',
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        observe: 'response',
      }),
    )
      .then()
      .catch((e) => {
        if (e.status === 404) {
          this.userNotificationService.addNotification(`${notificationBody}: No data found.`, false);
        } else {
          this.logger.httpError(this.constructor.name, 'ReportEliminationChart', e);
          this.userNotificationService.addUpdateNotification('ReportEliminationChart', `${notificationBody}: ${e.message}`, true);
        }
      });

    return true;
  }

  public async GetSearchStrategyHeaderCount(StudySearchStrategyId: number): Promise<StudySearchStrategy> {
    const result = <StudySearchStrategy>await firstValueFrom(this.GetSearchStrategyHeaderCount$(StudySearchStrategyId)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetSearchStrategyHeaderCount', e);
    });
    return result;
  }

  public GetSearchStrategyHeaderCount$(StudySearchStrategyId: number): any {
    this.logger.info(this.constructor.name, 'GetSearchStrategyHeaderCount', 'Start');
    return this.http.get<StudySearchStrategy>(`/api/Study/SearchStrategyHeaderCount/${StudySearchStrategyId}`);
  }

  public async GetStudyViewsByStudyId(studyId: number): Promise<StudyViews> {
    this.logger.info(this.constructor.name, 'GetStudyViewsByStudyId', 'Start');
    const result = <StudyViews>await firstValueFrom(this.http.get<StudyViews>(`/api/Study/GetStudyViewsByStudyId/${studyId}`)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetStudyViewsByStudyId', e);
    });
    return result;
  }

  public async AddAndCheckStudyViews(studyId: number): Promise<number> {
    this.logger.info(this.constructor.name, 'AddAndCheckStudyViews', 'Start');
    const result = <number>await firstValueFrom(this.http.post<number>(`/api/Study/AddAndCheckStudyViews/${studyId}`, null)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'AddAndCheckStudyViews', e);
    });
    return result;
  }

  public async GetCompanyNameFilterResultDataSet(filter: any): Promise<any[]> {
    this.logger.info(this.constructor.name, 'GetCompanyNameFilterResultDataSet', 'Start');
    const result = <any[]>await firstValueFrom(this.http.post<any[]>(`/api/Study/GetCompanyNameFilterResultDataSet/`, filter)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetCompanyNameFilterResultDataSet', e);
    });
    return result;
  }

  public async DeleteStudyById(studyId: number): Promise<boolean> {
    this.logger.info(this.constructor.name, 'DeleteStudyById', 'Start');
    const result = <boolean>await firstValueFrom(this.http.post<boolean>(`/api/Study/DeleteStudyById/${studyId}`, null)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'DeleteStudyById', e);
    });
    return result;
  }

  public async UpdateSearchStrategy(studySearchStartegy: StudySearchStrategy): Promise<StudySearchStrategy> {
    this.logger.info(this.constructor.name, 'UpdateSearchStrategy', 'Start', studySearchStartegy);

    let savedStudySearchStrategy = null;
    if (studySearchStartegy.studySearchStrategyId) {
      // Update
      savedStudySearchStrategy = <StudySearchStrategy>await firstValueFrom(
        this.http.put<StudySearchStrategy>('/api/study/SearchStrategy', studySearchStartegy),
      ).catch((e) => {
        this.logger.httpError(this.constructor.name, 'UpdateSearchStrategy', e);
        if (e.status === 401) {
          this.router.navigate(['nostudyaccess']);
        }
      });
    }

    return savedStudySearchStrategy;
  }

  public async GetSearchStrategyById(studySearchStrategyId: number): Promise<StudySearchStrategy> {
    this.logger.info(this.constructor.name, 'GetSearchStrategy', 'Start', studySearchStrategyId);

    let studySearchStrategy = null;
    if (studySearchStrategyId > 0) {
      // Update
      studySearchStrategy = <StudySearchStrategy>await firstValueFrom(
        this.http.get<StudySearchStrategy>(`/api/study/SearchStrategyById/${studySearchStrategyId}`),
      ).catch((e) => {
        this.logger.httpError(this.constructor.name, 'GetSearchStrategy', e);
        if (e.status === 401) {
          this.router.navigate(['nostudyaccess']);
        }
      });
    }

    return studySearchStrategy;
  }

  public async ReportEngagementCode(studyId: number): Promise<boolean> {
    this.logger.info(this.constructor.name, 'ReportEngagementCode', 'Start');

    if (studyId === undefined) {
      // caller should be handling this now, but I'll go ahead and leave this in here...
      this.userNotificationService.addUpdateNotification(
        'ReportEngagementCode',
        `Engagement Code Report: Engagement Code Report does not exist.`,
        true,
      );
      return true;
    }

    const notificationBody = `Study ${studyId} Engagement Code Report`;

    await firstValueFrom(
      this.http.post('/api/study/ReportEngagementCode/', studyId, {
        responseType: 'blob',
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        observe: 'response',
      }),
    )
      .then()
      .catch((e) => {
        if (e.status === 404) {
          this.userNotificationService.addNotification(`${notificationBody}: No data found.`, false);
        } else {
          this.logger.httpError(this.constructor.name, 'ReportEngagementCode', e);
          this.userNotificationService.addUpdateNotification('ReportEngagementCode', `${notificationBody}: ${e.message}`, true);
        }
      });

    return true;
  }

  public async IsConfidentialStudyAccessible(userId: number, studyId: number): Promise<boolean> {
    this.logger.info(this.constructor.name, 'IsConfidentialStudyAccessible', 'Start');
    let isStudyAccessible: boolean;
    try {
      isStudyAccessible = <boolean>await firstValueFrom(
        this.http.get<boolean>(`/api/study/IsConfidentialStudyAccessible/${userId}/${studyId}`),
      ).catch((e) => {
        this.logger.httpError(this.constructor.name, 'IsConfidentialStudyAccessible', e);
      });
      return isStudyAccessible;
    } finally {
      this.logger.info(this.constructor.name, 'IsConfidentialStudyAccessible', 'End');
    }
  }

  public async UpdateStudySetting(studyId: number, studySettingId: number) {
    this.logger.info(this.constructor.name, 'UpdateStudySetting', 'Start', studyId);

    await firstValueFrom(this.http.patch<Study>('/api/study/UpdateStudySetting', { studyId: studyId, studySettingId: studySettingId })).catch((e) => {
      this.logger.httpError(this.constructor.name, 'UpdateStudySetting', e);
      if (e.status === 401) {
        this.router.navigate(['nostudyaccess']);
      }
    });
  }

  public getFiltersForStudy$(dbIds: number[]): Observable<Lookup[]> {
    const databaseIds = dbIds.length === 0 ? '' : `?dbIds=${_.join(dbIds, '&dbIds=')}`;
    return this.http.get<Lookup[]>(`/api/Study/GetFiltersForStudy${databaseIds}`);
  }

  public GetStudyStatusById(studyId: number): Observable<Study> {
    return this.http.get<Study>(`/api/study/GetStudyStatus/${studyId}`) as Observable<Study>;
  }

  public async GetTabWiseComparableCount(studySearchStrategyId: number, stageId: number): Promise<any> {
    const result = <any>await firstValueFrom(this.GetTabWiseComparableCount$(studySearchStrategyId, stageId)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetTabWiseComparableCount', e);
    });
    return result;
  }
  public GetTabWiseComparableCount$(studySearchStrategyId: number, stageId: number): any {
    this.logger.info(this.constructor.name, 'GetTabWiseComparableCount$', 'Start');
    return this.http.get<any>(`/api/Study/GetTabWiseComparableCount/${studySearchStrategyId}/${stageId}`);
  }
  public async getRejectedComparableWithoutDecisionCriteria(studySearchStrategyId: number, stageId: number): Promise<any[]> {
    let comparableList: any;
    if (studySearchStrategyId) {
      comparableList = await firstValueFrom(
        this.http.get<any[]>(`/api/Study/GetRejectedComparableWithoutDecisionCriteria/${studySearchStrategyId}/${stageId}`),
      ).catch((e) => {
        this.logger.httpError(this.constructor.name, 'getRejectedComparableWithoutDecisionCriteria', e);
      });
    }
    return comparableList;
  }

}
